import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { vitecompanyserviceurl } from "src/constants/global-const";
import { addAuthHeader } from "src/utils/auth";
import { attachRefreshInterceptor } from "src/utils/axiosRefreshInterceptor";

let iavInstance: AxiosInstance;
let iavProducerInstance: AxiosInstance;

// MEDVOL: IAV V4 — reorganized under existing /api/v1/oms/invoice domain per team confirmation
const initInstance = () => {
  iavInstance = axios.create({ baseURL: vitecompanyserviceurl + "/api/v1/oms/invoice" });
  // MEDVOL: IAV V4 — write actions (approve, reject, dispute doc, debit note) go through the
  // /event/invoice producer routes, same event-driven pattern as Invoice entry/approval.
  // The producer only validates and publishes the event (200 with eventStreamCreated.streamId);
  // the V2 consumer replays it to /api/v1/oms/invoice/<action>, which does the DB write
  // asynchronously. The invoice ID is NOT in the path: the body is
  //   { primary_key: invoice_id, data: { invoiceId, ...payload } }   (see iavEventBody)
  // primary_key is required: the /event gate uses it to check stream `invoice_<invoice_id>`
  // (409 when missing or when the stream has a Failed/Queue event, 400 when it doesn't match
  // the invoice). event_type is set by the backend. Callers gate on useEventStatus first.
  // Read-only calls (ops queue, revoke reasons), the presigned upload URL and reassign (no
  // event, by design) stay on iavInstance with the invoice ID in the path.
  iavProducerInstance = axios.create({ baseURL: vitecompanyserviceurl + "/event/invoice" });

  // Without the token the API Gateway authorizer rejects every call (401 UnauthorizedException).
  iavInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
  iavProducerInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => addAuthHeader(config));
  attachRefreshInterceptor(iavInstance);
  attachRefreshInterceptor(iavProducerInstance);
};

initInstance();

// Producer body for every /event/invoice/ops/* call.
const iavEventBody = (invoiceId: number, data: Record<string, any> = {}) => ({
  primary_key: invoiceId,
  data: { invoiceId, ...data },
});

// Message to show for a failed IAV write: the backend's 400/404/409 `message` when there is one.
// Returns null for a blocked stream, which useEventStatus has already toasted.
export const getIavErrorMessage = (err: any, fallback: string): string | null => {
  if (axios.isAxiosError(err)) return (err.response?.data as any)?.message ?? fallback;
  if (/^Event (is|became) blocked/.test(err?.message ?? "")) return null;
  return err?.message || fallback;
};

// Presigned S3 upload — same pattern as uploadFileToPresignedUrl in order-api.ts.
const uploadFileToPresignedUrl = (file: File, uploadUrl: string): Promise<Response> => {
  return fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
};

// Ops Queue
export const getOpsQueue = async (params: Record<string, any>): Promise<AxiosResponse> => {
  return iavInstance.get("/ops/queue", { params });
};

export const getRevokeReasons = async (): Promise<AxiosResponse> => {
  return iavInstance.get("/ops/revoke-reasons");
};

export const approveStampSign = async (invoiceId: number, payload: any): Promise<AxiosResponse> => {
  return iavProducerInstance.post("/ops/approve", iavEventBody(invoiceId, payload));
};

export const rejectStampSign = async (invoiceId: number, payload: any): Promise<AxiosResponse> => {
  return iavProducerInstance.post("/ops/reject", iavEventBody(invoiceId, payload));
};

// Presigned-URL upload flow, step 1 — request a PUT URL for the dispute document.
export const getDisputeDocUploadUrl = async (
  invoiceId: number,
  filename: string,
): Promise<AxiosResponse<{ data: { uploadUrl: string; s3Key: string } }>> => {
  return iavInstance.post(`/ops/${invoiceId}/upload-dispute-doc/upload-url`, { filename });
};

// Full upload: gets the presigned URL, PUTs the file to S3, then publishes the confirm event
// with the s3Key.
export const uploadDisputeDoc = async (invoiceId: number, file: File): Promise<AxiosResponse> => {
  const {
    data: { data: uploadTarget },
  } = await getDisputeDocUploadUrl(invoiceId, file.name);
  const s3Response = await uploadFileToPresignedUrl(file, uploadTarget.uploadUrl);
  // fetch doesn't reject on HTTP errors; don't publish an event for a file that never reached S3.
  if (!s3Response.ok) {
    throw new Error(`Document upload failed (${s3Response.status})`);
  }
  return iavProducerInstance.post("/ops/upload-dispute-doc", iavEventBody(invoiceId, { s3_key: uploadTarget.s3Key }));
};

export const initiateDebitNote = async (invoiceId: number, payload: any): Promise<AxiosResponse> => {
  return iavProducerInstance.post("/ops/initiate-debit-note", iavEventBody(invoiceId, payload));
};

export const reassignInvoice = async (invoiceId: number, payload: any): Promise<AxiosResponse> => {
  return iavInstance.post(`/ops/${invoiceId}/reassign`, payload);
};
